<!-- Inline-форма создания и редактирования позиции каталога. -->
<template>
  <form
    :id="formId"
    class="catalog-item-form"
    @submit.prevent="save"
  >
    <div class="catalog-item-form__grid">
      <div class="catalog-item-form__field">
        <base-label
          :required="true"
          :filled="isFieldFilled('base_type')"
        >
          Тип
        </base-label>
        <base-select
          v-model="form.base_type"
          :options="baseTypeOptions"
          :clearable="false"
          :invalid="errors.base_type"
          @change="clearError('base_type')"
        />
        <span v-if="errors.base_type" class="error-message">
          Выберите тип
        </span>
      </div>

      <div class="catalog-item-form__field">
        <NumeratorField
          v-model="form.number"
          target-model="Catalog::Item"
          :editable="!initialData"
          placeholder="Артикул или код"
          @update:modelValue="clearError('number')"
        />
      </div>

      <div class="catalog-item-form__field catalog-item-form__field--wide">
        <base-label
          :required="true"
          :filled="isFieldFilled('name')"
        >
          Название
        </base-label>
        <base-input
          v-model="form.name"
          placeholder="Название работы, запчасти или материала"
          :invalid="errors.name"
          @input="clearError('name')"
        />
        <span v-if="errors.name" class="error-message">
          Укажите название
        </span>
      </div>

      <div class="catalog-item-form__field catalog-item-form__field--wide">
        <base-label
          :required="true"
          :filled="usageScopesFilled"
        >
          Область применения
        </base-label>
        <div class="catalog-item-form__scope-list">
          <base-checkbox
            v-for="option in usageScopeOptions"
            :key="option.value"
            :model-value="usageScopeIsSelected(option.value)"
            :disabled="usageScopeIsDisabled(option.value)"
            @update:modelValue="toggleUsageScope(option.value, $event)"
          >
            {{ option.label }}
          </base-checkbox>
        </div>
        <span v-if="errors.usage_scopes" class="error-message">
          Выберите хотя бы одну область
        </span>
      </div>

      <div class="catalog-item-form__field">
        <base-label>Единица измерения</base-label>
        <base-input
          v-model="form.unit"
          placeholder="шт"
        />
      </div>

      <div class="catalog-item-form__field">
        <base-label>Цена</base-label>
        <base-input
          v-model="form.unit_price"
          type="number"
          min="0"
          step="0.01"
          placeholder="0.00"
        />
      </div>

      <VatFields
        v-model:vatKind="form.vat_kind"
        v-model:vatRate="form.vat_rate"
        v-model:vatCalculationType="form.vat_calculation_type"
        :vat-kind-options="vatKindOptions"
        :vat-calculation-type-options="vatCalculationTypeOptions"
        :errors="errors"
        field-class="catalog-item-form__field"
        :vat-kind-filled="isFieldFilled('vat_kind')"
        :vat-calculation-type-filled="
          isFieldFilled('vat_calculation_type')
        "
        vat-kind-label="НДС"
        vat-rate-label="Ставка (%)"
        vat-rate-error-text="Укажите корректную ставку"
        vat-calculation-type-label="Расчёт НДС"
        vat-calculation-type-error-text="Выберите расчёт НДС"
        @clear-error="clearError"
      />
    </div>

    <div v-if="showActions" class="catalog-item-form__actions">
      <base-primary-button
        type="submit"
        class="catalog-item-form__button"
        :disabled="isLoading"
        :loading="isLoading"
      >
        Сохранить
      </base-primary-button>
      <base-button
        type="button"
        class="catalog-item-form__button"
        @click="$emit('cancel')"
      >
        Отмена
      </base-button>
    </div>
  </form>
</template>

<script>
import { useNotificationStore } from "@/stores/notificationStore";
import NumeratorField from "@/components/Numerator/Field.vue";
import VatFields from "@/components/Shared/VatFields.vue";

import {
  payloadVatRate,
  vatRateIsInvalid,
} from "@/utils/vatUtils";
import { normalizeCatalogItemUsageScopes } from "@/utils/catalogItemUsageScopeUtils";

export default {
  name: "CatalogItemForm",
  components: {
    NumeratorField,
    VatFields,
  },

  props: {
    initialData: {
      type: Object,
      default: null,
    },
    baseTypeOptions: {
      type: Array,
      required: true,
    },
    usageScopeOptions: {
      type: Array,
      required: true,
    },
    vatKindOptions: {
      type: Array,
      required: true,
    },
    vatCalculationTypeOptions: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    formId: {
      type: String,
      default: null,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["cancel", "save"],

  data() {
    return {
      form: {
        base_type: "service",
        number: "",
        name: "",
        unit: "",
        unit_price: "",
        usage_scopes: ["repair"],
        vat_kind: "no_vat",
        vat_rate: "",
        vat_calculation_type: "included",
      },
      errors: {
        base_type: false,
        name: false,
        usage_scopes: false,
        vat_kind: false,
        vat_rate: false,
        vat_calculation_type: false,
      },
    };
  },

  watch: {
    "form.base_type": {
      handler() {
        this.form.usage_scopes = normalizeCatalogItemUsageScopes(
          this.form.usage_scopes,
          this.form.base_type
        );
        this.clearError("usage_scopes");
      },
    },
  },

  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    usageScopesFilled() {
      return this.form.usage_scopes.length > 0;
    },
  },

  methods: {
    initForm() {
      if (!this.initialData) return;

      this.form = {
        base_type: this.initialData.base_type || "service",
        number: this.initialData.number || "",
        name: this.initialData.name || "",
        unit: this.initialData.unit || "",
        unit_price: this.initialData.unit_price ?? "",
        usage_scopes: normalizeCatalogItemUsageScopes(
          this.initialData.usage_scopes,
          this.initialData.base_type
        ),
        vat_kind: this.initialData.vat_kind || "no_vat",
        vat_rate: this.initialData.vat_rate ?? "",
        vat_calculation_type:
          this.initialData.vat_calculation_type || "included",
      };
    },

    clearError(field) {
      if (!this.errors[field]) return;
      this.errors[field] = false;
    },

    isFieldFilled(field) {
      const value = this.form[field];
      return (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
      );
    },

    validate() {
      this.errors = {
        base_type: !this.form.base_type,
        name: !String(this.form.name || "").trim(),
        usage_scopes: this.form.usage_scopes.length === 0,
        vat_kind: !this.form.vat_kind,
        vat_rate: this.vatRateIsInvalid(),
        vat_calculation_type: !this.form.vat_calculation_type,
      };

      const hasErrors = Object.values(this.errors).some(Boolean);
      if (hasErrors) {
        this.notificationStore.showError(
          "Пожалуйста, заполните обязательные поля"
        );
      }

      return !hasErrors;
    },

    vatRateIsInvalid() {
      return vatRateIsInvalid(this.form.vat_kind, this.form.vat_rate);
    },

    usageScopeIsSelected(scope) {
      return this.form.usage_scopes.includes(scope);
    },

    usageScopeIsDisabled(scope) {
      return this.form.base_type === "service" && scope !== "repair";
    },

    toggleUsageScope(scope, isSelected) {
      if (this.usageScopeIsDisabled(scope)) return;

      if (isSelected && !this.usageScopeIsSelected(scope)) {
        this.form.usage_scopes = [...this.form.usage_scopes, scope];
      }

      if (!isSelected) {
        this.form.usage_scopes = this.form.usage_scopes.filter(
          (value) => value !== scope
        );
      }

      this.clearError("usage_scopes");
    },

    save() {
      if (!this.validate()) return;

      this.$emit("save", {
        base_type: this.form.base_type,
        number: this.emptyToNull(this.form.number),
        name: this.form.name,
        unit: this.emptyToNull(this.form.unit),
        unit_price: this.emptyToNull(this.form.unit_price),
        vat_kind: this.form.vat_kind,
        vat_rate: payloadVatRate(this.form.vat_kind, this.form.vat_rate),
        vat_calculation_type: this.form.vat_calculation_type,
        usage_scopes: normalizeCatalogItemUsageScopes(
          this.form.usage_scopes,
          this.form.base_type
        ),
      });
    },

    emptyToNull(value) {
      return value === "" || value === undefined ? null : value;
    },
  },

  created() {
    this.initForm();
  },
};
</script>

<style scoped>
.catalog-item-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: var(--base-in-indent);
}

.catalog-item-form__field {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.catalog-item-form__field--wide {
  grid-column: 1 / -1;
}

.catalog-item-form__scope-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  min-height: 36px;
  align-items: center;
}

.catalog-item-form__actions {
  display: flex;
  gap: 8px;
}

.catalog-item-form__button {
  padding: 6px 14px;
  font-size: 13px;
}

.error-message {
  margin-top: 5px;
  color: var(--color-danger);
  font-size: 12px;
}

@media (max-width: 640px) {
  .catalog-item-form__grid {
    grid-template-columns: 1fr;
  }
}
</style>
